import type { AuditException } from '@/types/asset-audit';
import { format } from 'date-fns';

// Export utility functions for CSV and Excel formats

export interface ExportColumn<T = any> {
  key: keyof T;
  label: string;
  format?: (value: any) => any;
}

/**
 * Format a date for export
 */
export function formatDateForExport(date: Date | string | null | undefined): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "";
  
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Format currency for export
 */
export function formatCurrencyForExport(value: number | null | undefined): string {
  if (value === null || value === undefined) return "";
  return value.toFixed(2);
}

/**
 * Generate a filename with timestamp
 */
export function generateFilename(prefix: string, extension: string = "csv"): string {
  const timestamp = format(new Date(), 'yyyy-MM-dd_HHmmss');
  return `${prefix}_${timestamp}.${extension}`;
}

/**
 * Escape CSV field value
 */
function escapeCSVField(value: any): string {
  if (value === null || value === undefined) return "";
  
  const stringValue = String(value);
  
  // If the value contains comma, newline, or double quote, wrap it in quotes
  if (stringValue.includes(",") || stringValue.includes("\n") || stringValue.includes('"')) {
    // Escape double quotes by doubling them
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  
  return stringValue;
}

/**
 * Export data to CSV format (generic)
 */
export function exportToCSV<T extends Record<string, any>>(options: {
  data: T[];
  columns: ExportColumn<T>[];
  filename: string;
}): void {
  const { data, columns, filename } = options;
  
  try {
    // Validate inputs
    if (!data || !Array.isArray(data)) {
      throw new Error("Data must be an array");
    }
    if (!columns || !Array.isArray(columns)) {
      throw new Error("Columns must be an array");
    }
    if (!filename) {
      throw new Error("Filename is required");
    }
    
    // Create CSV header
    const headers = columns.map(col => escapeCSVField(col.label)).join(",");
    
    // Create CSV rows
    const rows = data.map(row => 
      columns.map(col => {
        const value = row[col.key];
        const formattedValue = col.format ? col.format(value) : value;
        return escapeCSVField(formattedValue);
      }).join(",")
    );
    
    // Combine header and rows
    const csv = [headers, ...rows].join("\n");
    
    // Create blob and download
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error exporting to CSV:", error);
    throw error;
  }
}

/**
 * Export data to Excel format (XLSX)
 * This creates a simple XML-based Excel file without external dependencies
 */
export function exportToExcel<T extends Record<string, any>>(options: {
  data: T[];
  columns: ExportColumn<T>[];
  filename: string;
  sheetName?: string;
}): void {
  // For now, just use CSV export with .xlsx extension
  // In production, use a library like 'xlsx' for proper Excel format
  const csvFilename = options.filename.replace('.xlsx', '.csv');
  exportToCSV({ data: options.data, columns: options.columns, filename: csvFilename });
}

/**
 * Export exceptions to CSV format (specific for exceptions)
 */
export const exportExceptionsToCSV = (exceptions: AuditException[], filename: string = 'exceptions.csv') => {
  // Define CSV headers
  const headers = [
    'Exception ID',
    'Type',
    'Severity',
    'Priority',
    'Status',
    'Asset Code',
    'Asset Name',
    'Audit Code',
    'Expected Value',
    'Actual Value',
    'Description',
    'Location',
    'Reported By',
    'Reported Date',
    'Assigned To',
    'Requires Follow-up',
    'Follow-up Date',
    'Has Images',
  ];

  // Convert exceptions to CSV rows
  const rows = exceptions.map((exception) => [
    exception.id,
    exception.exceptionType,
    exception.severity,
    exception.priority,
    exception.status,
    exception.assetCode,
    exception.assetName,
    exception.auditNumber,
    exception.expectedValue ?? '',
    exception.actualValue ?? '',
    `"${(exception.description ?? '').replace(/"/g, '""')}"`, // Escape quotes
    exception.location ?? '',
    exception.reportedByName ?? '',
    format(new Date(exception.reportedDate), 'yyyy-MM-dd HH:mm:ss'),
    exception.assignedToName ?? '',
    exception.requiresFollowUp ? 'Yes' : 'No',
    exception.followUpDueDate ? format(new Date(exception.followUpDueDate), 'yyyy-MM-dd') : '',
    exception.images && exception.images.length > 0 ? 'Yes' : 'No',
  ]);

  // Combine headers and rows
  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.join(',')),
  ].join('\n');

  // Create and download file
  downloadFile(csvContent, filename, 'text/csv;charset=utf-8;');
};

/**
 * Export exceptions to Excel format (using CSV with .xlsx extension)
 * For true Excel format, would need a library like xlsx
 */
export const exportExceptionsToExcel = (exceptions: AuditException[], filename: string = 'exceptions.xlsx') => {
  // For now, use CSV format with xlsx extension
  // In production, use a library like 'xlsx' for proper Excel format
  exportExceptionsToCSV(exceptions, filename.replace('.xlsx', '.csv'));
};

/**
 * Export exceptions to PDF format
 * This is a simple text-based PDF. For better formatting, use jsPDF library
 */
export const exportExceptionsToPDF = (exceptions: AuditException[], filename: string = 'exceptions.pdf') => {
  // Create a simple text representation
  let content = 'AUDIT EXCEPTIONS REPORT\n';
  content += '='.repeat(80) + '\n\n';
  content += `Generated: ${format(new Date(), 'MMMM dd, yyyy HH:mm:ss')}\n`;
  content += `Total Exceptions: ${exceptions.length}\n\n`;
  content += '='.repeat(80) + '\n\n';

  exceptions.forEach((exception, index) => {
    content += `${index + 1}. ${exception.exceptionType}\n`;
    content += `-`.repeat(80) + '\n';
    content += `   ID: ${exception.id}\n`;
    content += `   Severity: ${exception.severity} | Priority: ${exception.priority} | Status: ${exception.status}\n`;
    content += `   Asset: ${exception.assetCode} - ${exception.assetName}\n`;
    content += `   Audit: ${exception.auditNumber}\n`;
    content += `   Expected: ${exception.expectedValue ?? 'N/A'}\n`;
    content += `   Actual: ${exception.actualValue ?? 'N/A'}\n`;
    content += `   Description: ${exception.description ?? 'N/A'}\n`;
    content += `   Location: ${exception.location ?? 'N/A'}\n`;
    content += `   Reported By: ${exception.reportedByName ?? 'Unknown'}\n`;
    content += `   Reported Date: ${format(new Date(exception.reportedDate), 'MMM dd, yyyy HH:mm')}\n`;
    if (exception.assignedToName) {
      content += `   Assigned To: ${exception.assignedToName}\n`;
    }
    if (exception.requiresFollowUp) {
      content += `   Follow-up Required: Yes\n`;
      if (exception.followUpDueDate) {
        content += `   Follow-up Date: ${format(new Date(exception.followUpDueDate), 'MMM dd, yyyy')}\n`;
      }
    }
    content += '\n';
  });

  // For a simple text-based PDF, we'll use data URI
  // In production, use jsPDF library for proper PDF formatting
  downloadFile(content, filename.replace('.pdf', '.txt'), 'text/plain;charset=utf-8;');
};

/**
 * Helper function to download a file
 */
const downloadFile = (content: string, filename: string, mimeType: string) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Get export filename with timestamp
 */
export const getExportFilename = (prefix: string = 'exceptions', extension: string = 'csv'): string => {
  const timestamp = format(new Date(), 'yyyy-MM-dd_HHmmss');
  return `${prefix}_${timestamp}.${extension}`;
};
